import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { Form } from 'react-final-form';
import { Button, ButtonWithLoading } from '@spark/ui-components/components/Button';
import TanStackTable from '@spark/ui-components/components/TanStackTable/TanStackTable';
import FilterPanel from '@spark/ui-components/components/FilterPanel';
import CollapsibleCard from '@spark/ui-components/components/CollapsibleCard';
import { FormButtonToolbar, FormContainer } from '@spark/ui-components/components/form/FormElements';
import DynamicForm from '@spark/ui-components/components/form/DynamicForm';
import { notification } from '@spark/ui-components/utils/notification';
import {
  getInfracaoMotoristaPremiacaoById,
  createInfracaoMotoristaPremiacao,
  updateInfracaoMotoristaPremiacao,
  deleteInfracaoMotoristaPremiacao,
  importarInfracaoMotoristaPremiacoes,
  exportarLayoutImportacaoInfracaoMotoristaComRegistros
} from '@spark/services/premiacao';
import { infracaoMotoristaFilterConfig } from '../config/infracaoMotoristaFilterConfig';
import { getInfracaoMotoristaColumns } from '../config/infracaoMotoristaColumns';
import {
  emptyInfracaoMotoristaForm,
  infracaoMotoristaFormSections,
  infracaoMotoristaFormStructure,
  validateInfracaoMotoristaForm
} from '../config/infracaoMotoristaFormConfig';
import { LAYOUT_IMPORTACAO_OCORRENCIAS } from '../config/premiacaoImportLayouts';
import { usePremiacaoInfracaoMotoristaSelection } from '../hooks/usePremiacaoInfracaoMotoristaSelection';
import PremiacaoImportacaoExcelModal from './PremiacaoImportacaoExcelModal';

const pick = (data, ...keys) => {
  for (let index = 0; index < keys.length; index += 1) {
    const key = keys[index];
    if (data?.[key] !== undefined && data?.[key] !== null) {
      return data[key];
    }
  }
  return undefined;
};

const extractValue = (value) => {
  if (value === null || value === undefined) return value;
  if (typeof value === 'object' && value.value !== undefined) return value.value;
  return value;
};

const parseNumber = (value) => {
  const rawValue = extractValue(value);
  if (rawValue === '' || rawValue === null || rawValue === undefined) return null;
  const parsed = Number(rawValue);
  return Number.isNaN(parsed) ? null : parsed;
};

const normalizeText = (value) => {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  return trimmed.length > 0 ? trimmed : null;
};

const toDigitsOnly = (value) => (value ? String(value).replace(/\D/g, '') : undefined);

const toRowModel = (item) => ({
  id: pick(item, 'id', 'Id'),
  cpfMotorista: pick(item, 'cpfMotorista', 'CpfMotorista') || '',
  mesFechamento: pick(item, 'mesFechamento', 'MesFechamento'),
  anoFechamento: pick(item, 'anoFechamento', 'AnoFechamento'),
  idInfracao: pick(item, 'idInfracao', 'IdInfracao'),
  descricaoInfracao: pick(item, 'descricaoInfracao', 'DescricaoInfracao') || ''
});

const mapApiToFormValues = (data) => ({
  id: pick(data, 'id', 'Id') ?? '',
  cpfMotorista: pick(data, 'cpfMotorista', 'CpfMotorista') || '',
  mesFechamento: pick(data, 'mesFechamento', 'MesFechamento') ?? '',
  anoFechamento: pick(data, 'anoFechamento', 'AnoFechamento') ?? '',
  idInfracao: pick(data, 'idInfracao', 'IdInfracao') ?? ''
});

const buildPayload = (values) => ({
  CpfMotorista: toDigitsOnly(values.cpfMotorista) || normalizeText(values.cpfMotorista),
  MesFechamento: parseNumber(values.mesFechamento),
  AnoFechamento: parseNumber(values.anoFechamento),
  IdInfracao: parseNumber(values.idInfracao)
});

const PremiacaoInfracaoMotoristaDashboardModal = ({ show, onHide }) => {
  const [editingId, setEditingId] = useState(null);
  const [formInitialValues, setFormInitialValues] = useState(emptyInfracaoMotoristaForm);
  const [saving, setSaving] = useState(false);
  const [loadingEditId, setLoadingEditId] = useState(null);
  const [loadingDeleteId, setLoadingDeleteId] = useState(null);
  const [isFormCollapsed, setIsFormCollapsed] = useState(true);
  const [showImportacaoModal, setShowImportacaoModal] = useState(false);
  const {
    motoristas,
    loading,
    paginaAtual,
    quantidadePorPagina,
    totalRegistros,
    handleFiltrosChange,
    handlePaginationChange,
    reload
  } = usePremiacaoInfracaoMotoristaSelection(show, null);

  useEffect(() => {
    if (!show) {
      setEditingId(null);
      setFormInitialValues(emptyInfracaoMotoristaForm);
      setLoadingEditId(null);
      setLoadingDeleteId(null);
      setIsFormCollapsed(true);
      setShowImportacaoModal(false);
    }
  }, [show]);

  const handleEdit = useCallback(async (id) => {
    if (!id || loadingEditId === id) return;

    try {
      setLoadingEditId(id);
      const data = await getInfracaoMotoristaPremiacaoById(id);
      setEditingId(id);
      setFormInitialValues(mapApiToFormValues(data));
      setIsFormCollapsed(false);
    } catch (error) {
      notification.error('Erro ao carregar ocorrencia do motorista');
      console.error('Erro ao carregar ocorrencia do motorista:', error);
    } finally {
      setLoadingEditId(null);
    }
  }, [loadingEditId]);

  const handleDelete = useCallback(async (id) => {
    if (!id || loadingDeleteId === id) return;

    try {
      setLoadingDeleteId(id);
      await deleteInfracaoMotoristaPremiacao(id);
      notification.success('Ocorrencia excluida com sucesso!');
      if (editingId === id) {
        setEditingId(null);
        setFormInitialValues(emptyInfracaoMotoristaForm);
      }
      reload();
    } catch (error) {
      notification.error('Erro ao excluir ocorrencia do motorista');
      console.error('Erro ao excluir ocorrencia do motorista:', error);
    } finally {
      setLoadingDeleteId(null);
    }
  }, [editingId, loadingDeleteId, reload]);

  const handleClear = useCallback((form) => {
    setEditingId(null);
    setFormInitialValues(emptyInfracaoMotoristaForm);
    form.reset(emptyInfracaoMotoristaForm);
    setIsFormCollapsed(false);
  }, []);

  const handleSubmit = useCallback(async (values) => {
    const payload = buildPayload(values);

    try {
      setSaving(true);
      let data;

      if (values.id) {
        data = await updateInfracaoMotoristaPremiacao(values.id, payload);
        notification.success('Ocorrencia atualizada com sucesso!');
      } else {
        data = await createInfracaoMotoristaPremiacao(payload);
        notification.success('Ocorrencia criada com sucesso!');
      }

      setEditingId(pick(data, 'id', 'Id') || values.id || null);
      setFormInitialValues(mapApiToFormValues(data));
      reload();
      setIsFormCollapsed(false);
    } catch (error) {
      notification.error('Erro ao salvar ocorrencia do motorista');
      console.error('Erro ao salvar ocorrencia do motorista:', error);
    } finally {
      setSaving(false);
    }
  }, [reload]);

  const rows = Array.isArray(motoristas) ? motoristas.map(toRowModel) : [];
  const formKey = useMemo(() => (editingId ? `motorista-${editingId}` : 'motorista-create'), [editingId]);

  return (
    <>
      <Modal show={show} onHide={onHide} animation={false} size="xl" dialogClassName="modal-uniform-xl">
        <Modal.Header closeButton>
          <Modal.Title>Infrações × Motoristas</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <FilterPanel
            filters={infracaoMotoristaFilterConfig}
            onApply={handleFiltrosChange}
            title="Filtros de Ocorrencias"
            loading={loading}
            defaultCollapsed={false}
          />

          <TanStackTable
            key={`premiacao-infracao-motorista-dashboard-${paginaAtual}-${quantidadePorPagina}`}
            columns={getInfracaoMotoristaColumns(handleEdit, handleDelete, loadingEditId, loadingDeleteId)}
            data={rows}
            loading={loading}
            withPagination={true}
            pageSizeOptions={[5, 10, 25, 50]}
            enableRowColors={true}
            className="mt-3"
            manualPagination={true}
            totalItems={totalRegistros}
            controlledPagination={{
              pageIndex: Math.max(0, (paginaAtual || 1) - 1),
              pageSize: quantidadePorPagina || 10
            }}
            onPaginationChange={handlePaginationChange}
            enableExportExcel={true}
            exportFileName="premiacao-infracoes-motoristas"
          />

          <CollapsibleCard
            className="mt-3"
            title="Formulário de Ocorrência"
            isCollapsed={isFormCollapsed}
            onToggle={setIsFormCollapsed}
          >
            <Form
              key={formKey}
              onSubmit={handleSubmit}
              initialValues={formInitialValues}
              validate={validateInfracaoMotoristaForm}
            >
              {({ handleSubmit: onFormSubmit, form }) => (
                <FormContainer onSubmit={onFormSubmit}>
                  <DynamicForm
                    structure={infracaoMotoristaFormStructure}
                    sections={infracaoMotoristaFormSections()}
                    readOnly={false}
                  />

                  <FormButtonToolbar className="mt-3">
                    <Button variant="secondary" type="button" onClick={() => handleClear(form)} disabled={saving}>
                      Limpar
                    </Button>
                    <ButtonWithLoading variant="primary" type="submit" loading={saving}>
                      Salvar
                    </ButtonWithLoading>
                  </FormButtonToolbar>
                </FormContainer>
              )}
            </Form>
          </CollapsibleCard>
        </Modal.Body>

        <Modal.Footer>
          <div className="d-flex justify-content-between w-100">
            <div className="text-muted">
              {totalRegistros > 0 && (
                <span>
                  Exibindo {rows.length} de {totalRegistros} ocorrencia(s)
                </span>
              )}
            </div>
            <div className="d-flex gap-2">
              <Button variant="outline-primary" onClick={() => setShowImportacaoModal(true)}>
                Importar Ocorrencias
              </Button>
              <Button variant="secondary" onClick={onHide}>
                Fechar
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>

      <PremiacaoImportacaoExcelModal
        show={showImportacaoModal}
        onClose={() => setShowImportacaoModal(false)}
        onImportado={reload}
        title="Importar Ocorrências de Premiação"
        layoutColumns={LAYOUT_IMPORTACAO_OCORRENCIAS.columns}
        layoutObservacoes={LAYOUT_IMPORTACAO_OCORRENCIAS.observacoes}
        layoutFileName={LAYOUT_IMPORTACAO_OCORRENCIAS.fileName}
        layoutFileNameComRegistros={LAYOUT_IMPORTACAO_OCORRENCIAS.fileNameComRegistros}
        onImport={importarInfracaoMotoristaPremiacoes}
        onDownloadLayoutComRegistros={exportarLayoutImportacaoInfracaoMotoristaComRegistros}
      />
    </>
  );
};

export default PremiacaoInfracaoMotoristaDashboardModal;
